<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";

const props = defineProps<{ id: string }>();
const emit = defineEmits(["content-update"]);

/** 1) Access the definition data (read‑only) via the content manager */
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

const report = inject<any | null>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const logger = useLogger("Tabs");
const { applyTemplate, applyTemplateObject } = useTemplating(templateContext, { logger });
const templatedDefaultTab = computed(() => applyTemplate(definitionData.value?.defaultTab || ""));
const templatedChildren = computed(() => (definitionData.value?.children || []).map((child: any) => ({
  ...child,
  title: applyTemplate(child.title || "")
})));
const childIds = computed(() => templatedChildren.value.map((child: any) => child.id));
const activeTab = shallowRef("");

watch(
  [templatedDefaultTab, childIds],
  ([defaultTab, ids]) => {
    if (ids.includes(activeTab.value)) return;
    activeTab.value = ids.includes(defaultTab) ? defaultTab : "";
  },
  { immediate: true },
);


/** 3) Define commands for the context menu */
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Tabs container editors",
    commands: [
      {
        id: "edit",
        name: "Edit Tabs",
        description: "Edit tabs container settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: { ...definitionData.value },
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Tabs",
    icon: "lucide:app-window",
  },
  filter: ["Tab Item"],
});
</script>

<template>
  <Tabs
    v-model="activeTab"
    :class="cn($attrs.class ?? '')"
  >
    <TabsList>
      <template v-for="child in templatedChildren" :key="child.id">
        <TabsTrigger :value="child.id">
          {{ child.title }}
        </TabsTrigger>
      </template>
    </TabsList>
    <slot />
  </Tabs>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.TABS,
  editor: () => import("#bb/components/editor/container/tabs.vue"),
  icon: "lucide:app-window",
  type: "Tabs",
  description:
    "A container block for organizing content into tabs. Add Tab Item blocks inside.",
  default: {
    defaultTab: "",
    children: [],
  },
  categories: ["Layout"],
  filter: ["Tab Item"],
  order: 12003,
};
</script>

<i18n lang="yaml">
en:
  editTabsTitle: "Tabs"
</i18n>
